import React, { useCallback } from "react";
import { Form, Input, DatePicker, Button, Typography, Space, message } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useIntl } from "react-intl";
import { useUpdateApiKeyMutation } from "../../services/nestjs-base-api";
import { IApiKey, IUpdateApiKeyRequest } from "../../services/nestjs-base-api/types";
import dayjs from "dayjs";

const { Title } = Typography;

interface IProps {
    apiKey: IApiKey;
    onClose: () => void;
}

const ApiKeyEdit: React.FunctionComponent<IProps> = ({ apiKey, onClose }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [form] = Form.useForm();
    const [updateApiKey, { isLoading }] = useUpdateApiKeyMutation();

    const onFinish = useCallback(async (values: any) => {
        try {
            const payload: IUpdateApiKeyRequest = {
                name: values.name,
                expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
            };

            const result = await updateApiKey({
                jwtToken: user.jwtToken,
                organisationId: user.organisation.id,
                keyId: apiKey.id,
                payload,
            }).unwrap();

            message.success(result.message);
            onClose();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to update API key');
        }
    }, [user, updateApiKey, apiKey.id, onClose]);

    return (
        <div style={{ padding: '24px' }}>
            <Title level={4} style={{ marginBottom: '16px' }}>
                {intl.formatMessage({ id: 'apiKeys.edit.title' })}
            </Title>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                    name: apiKey.name,
                    expiresAt: apiKey.expiresAt ? dayjs(apiKey.expiresAt) : undefined,
                }}
            >
                <Form.Item
                    name="name"
                    label={intl.formatMessage({ id: 'apiKeys.create.nameLabel' })}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({ id: 'apiKeys.create.nameRequired' }),
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'apiKeys.create.namePlaceholder' })}
                    />
                </Form.Item>

                <Form.Item
                    name="expiresAt"
                    label={intl.formatMessage({ id: 'apiKeys.create.expiresLabel' })}
                >
                    <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        placeholder={intl.formatMessage({ id: 'apiKeys.create.expiresPlaceholder' })}
                        disabledDate={(current) => current && current < dayjs().endOf('day')}
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onClose}>
                            {intl.formatMessage({ id: 'common.cancelButton' })}
                        </Button>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            {intl.formatMessage({ id: 'apiKeys.edit.saveButton' })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ApiKeyEdit;